import { apiClient, ApiResponse, setAuthToken, removeAuthToken } from './api'
import { API_ENDPOINTS } from '@/constants/api'
import { connectWebSocket, setPendingAction, clearPendingAction } from './websocket'

// Session and Auth types
export interface SessionData {
  id: number
  customer?: {
    id: number
    ship_city?: string
    ship_state?: string
    ship_zip?: string
    payment_sources?: any[]
    addresses?: any[]
  }
  campaign?: {
    id: number
    switchable_campaigns?: any[]
  }
  step?: string
  step_type?: string
  step_id?: string
  step_slug?: string
  can_go_home?: boolean
  can_enter_exit_lifeline?: boolean
  started?: boolean
  result?: string
  selected_item?: {
    id: number
    billing_interval_days?: number
    [key: string]: any
  }
  valid_inputs?: any[]
  can_undo?: boolean
  is_step_preview?: boolean
  is_legacy?: boolean
  button_styles?: {
    confirm?: {
      label: string
      style: any
      style_type?: any
    }
  }
  welcome_message?: string
  label?: string
  subtitle?: string
  content?: string
  banner_content?: string
  enhanced_mode?: boolean
  is_customer_portal?: boolean
}

export interface AuthResult {
  auth_token: string
  session: SessionData
}

export interface SessionActionParams {
  action: 'verify' | 'match' | 'input' | 'jump' | 'email' | 'undo' | 'close' | 'mfa_send' | 'mfa_verify' | 'home'
  params: Record<string, any>
}

// Legacy types for backward compatibility
export interface LoginCredentials {
  email?: string
  username?: string
  password: string
  rememberMe?: boolean
}

export interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  acceptTerms: boolean
}

export interface AuthUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  permissions: string[]
  emailVerified: boolean
  createdAt: string
  updatedAt: string
}

export interface AuthResponse {
  user: AuthUser
  token: string
  refreshToken?: string
  expiresIn: number
}

// Authentication service functions
export const authService = {
  // === SESSION MANAGEMENT APIS ===

  // Start a new session
  async startSession(searchParam: Record<string, any>): Promise<SessionData> {
    const response = await apiClient.post<AuthResult>('/session/start/', searchParam, { timeout: 60000 })

    // Store auth token
    if (response.data.auth_token) {
      setAuthToken(response.data.auth_token)
    }

    return response.data.session
  },

  // Authorize session
  async authorizeSession(params: Record<string, any>): Promise<SessionData> {
    const response = await apiClient.post<AuthResult>('/session/auth/', params, { timeout: 60000 })

    // Store auth token
    if (response.data.auth_token) {
      setAuthToken(response.data.auth_token)
    }

    return response.data.session
  },

  // Get current session
  async getSession(): Promise<SessionData> {
    const response = await apiClient.get<SessionData>('/sessions/')
    return response.data
  },

  // Perform a session action. The server may answer synchronously (2xx with a
  // session payload) OR with a 202 Accepted, in which case the resolved session
  // is pushed back over the WebSocket. Either way, this resolves with the
  // session payload.
  async sessionAction(sessionId: number, actionData: SessionActionParams): Promise<SessionData> {
    console.log('session action', actionData)
    // WS must be live before posting — a 202 response can only be resolved by
    // a WS push, so a failed connect aborts the action entirely.
    await connectWebSocket()

    return new Promise<SessionData>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        console.error(`Timeout waiting for WebSocket update for action: ${actionData.action}`)
        clearPendingAction(new Error('Timeout after 60s.'))
      }, 60000)

      setPendingAction({ resolve, reject, timeoutId })

      const runRequest = async () => {
        try {
          const response = await apiClient.post<any>(
            `/sessions/${sessionId}/${actionData.action}/`,
            actionData.params,
            { timeout: 60000 }
          )
          if (response.status !== 202) {
            // Synchronous response — clear the pending slot so the WS handler
            // (if a stray message arrives later) doesn't double-resolve.
            clearPendingAction()
            resolve(response.data)
          }
          // For 202: leave the pending action in place. The WS message handler
          // will resolve it when the backend pushes the new session.
        } catch (err) {
          clearPendingAction()
          reject(err)
        }
      }
      runRequest()
    })
  },

  // Update session
  async updateSession(sessionId: number, data: Partial<SessionData>): Promise<SessionData> {
    const response = await apiClient.put<SessionData>(`/sessions/${sessionId}/`, data)
    return response.data
  },

  // Mark session as started
  async setSessionStarted(sessionId: number): Promise<SessionData> {
    const response = await apiClient.post<SessionData>(`/sessions/${sessionId}/start/`)
    return response.data
  },

  // === SESSION ACTION HELPERS ===

  async verify(sessionId: number, params: Record<string, any>): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'verify', params })
  },

  async match(sessionId: number, params: Record<string, any>): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'match', params })
  },

  async input(sessionId: number, params: Record<string, any>): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'input', params })
  },

  async jump(sessionId: number, params: Record<string, any>): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'jump', params })
  },

  async email(sessionId: number, params: Record<string, any>): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'email', params })
  },

  async undo(sessionId: number, params: Record<string, any> = {}): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'undo', params })
  },

  async close(sessionId: number): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'close', params: {} })
  },

  async mfaSend(sessionId: number, params: Record<string, any>): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'mfa_send', params })
  },

  async mfaVerify(sessionId: number, params: Record<string, any>): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'mfa_verify', params })
  },

  async home(sessionId: number, params: Record<string, any> = {}): Promise<SessionData> {
    return this.sessionAction(sessionId, { action: 'home', params })
  },

  // === STANDARD AUTH METHODS ===

  // Login method (backward compatible with both session auth and standard login)
  async login(credentials: LoginCredentials): Promise<SessionData | AuthResponse> {
    // If it looks like session auth, use compatible flow
    if (
      'username' in credentials ||
      Object.keys(credentials).some(key => !['email', 'password', 'rememberMe'].includes(key))
    ) {
      return this.authorizeSession(credentials) as Promise<SessionData>
    }

    // Otherwise use standard login flow
    const response = await apiClient.post<ApiResponse<AuthResponse>>(API_ENDPOINTS.AUTH.LOGIN, credentials)

    const authData = response.data.data
    setAuthToken(authData.token)

    return authData
  },

  // Logout method
  async logout(): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT)
    } catch (error) {
      // Continue with logout even if API call fails
    }

    removeAuthToken()

    // Dispatch logout event
    window.dispatchEvent(
      new CustomEvent('auth:logout', {
        detail: { reason: 'manual_logout' },
      })
    )
  },

  // Register new account
  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(API_ENDPOINTS.AUTH.REGISTER, userData)

    const authData = response.data.data
    setAuthToken(authData.token)

    if (authData.refreshToken) {
      localStorage.setItem('refresh_token', authData.refreshToken)
    }

    return authData
  },

  // Refresh authentication token
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await apiClient.post<ApiResponse<AuthResponse>>(API_ENDPOINTS.AUTH.REFRESH, { refreshToken })

    const authData = response.data.data
    setAuthToken(authData.token)

    if (authData.refreshToken) {
      localStorage.setItem('refresh_token', authData.refreshToken)
    }

    return authData
  },

  // Request password reset
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email,
    })
    return response.data.data
  },

  // Reset password with token
  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      token,
      password,
    })
    return response.data.data
  },

  // Verify email address
  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(API_ENDPOINTS.AUTH.VERIFY_EMAIL, { token })
    return response.data.data
  },

  // Resend email verification
  async resendVerification(): Promise<{ message: string }> {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(API_ENDPOINTS.AUTH.RESEND_VERIFICATION)
    return response.data.data
  },
}
